import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import opp2Image from '../assets/opportunities/opp2image.png';
import opp3Image from '../assets/opportunities/opp3image.png';

const INVEST_OPPORTUNITIES_URL = 'https://investoman.om/investment-opportunities';

const CARD_INDICES = [1, 2, 3, 4, 5, 6];

const STATIC_CARD_IMAGES = {
  2: opp2Image,
  3: opp3Image,
};

const pillStyle = {
  background: 'linear-gradient(90deg, rgba(66, 42, 126, 0.70) 0%, rgba(66, 42, 126, 0.49) 100%)',
  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
};

const CtaChevron = () => (
  <svg width="7" height="12" viewBox="0 0 10 14" fill="none" aria-hidden className="shrink-0 text-white">
    <path
      d="M1.5 1.5L7.5 7L1.5 12.5"
      stroke="currentColor"
      strokeWidth="1.29"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className="text-[#4C078C]">
    <path
      d="M15 6L9 12L15 18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden className="text-[#4C078C]">
    <path
      d="M9 6L15 12L9 18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function useVisibleOpportunityCount() {
  const [count, setCount] = useState(4);
  useEffect(() => {
    const mqLg = window.matchMedia('(min-width: 1024px)');
    const mqSm = window.matchMedia('(min-width: 640px)');
    const update = () => {
      if (mqLg.matches) setCount(4);
      else if (mqSm.matches) setCount(2);
      else setCount(1);
    };
    update();
    mqLg.addEventListener('change', update);
    mqSm.addEventListener('change', update);
    return () => {
      mqLg.removeEventListener('change', update);
      mqSm.removeEventListener('change', update);
    };
  }, []);
  return count;
}

const OpportunitiesInOman = () => {
  const { t, isRTL } = useLanguage();
  const visibleCount = useVisibleOpportunityCount();
  const [dataUriByCard, setDataUriByCard] = useState({ 1: null, 4: null, 5: null, 6: null });
  const [startIndex, setStartIndex] = useState(0);

  const total = CARD_INDICES.length;
  const maxStart = Math.max(0, total - visibleCount);

  useEffect(() => {
    setStartIndex((i) => Math.min(i, maxStart));
  }, [maxStart]);

  useEffect(() => {
    let cancelled = false;
    const base = `${process.env.PUBLIC_URL || ''}/opportdata`;
    const load = async () => {
      try {
        const [r1, r4, r5, r6] = await Promise.all([
          fetch(`${base}/opp1imageURI.txt`),
          fetch(`${base}/opp4imageURI.txt`),
          fetch(`${base}/opp5imageURI.txt`),
          fetch(`${base}/opp6imageURI.txt`),
        ]);
        const [t1, t4, t5, t6] = await Promise.all([r1.text(), r4.text(), r5.text(), r6.text()]);
        if (!cancelled) {
          setDataUriByCard({
            1: t1.trim(),
            4: t4.trim(),
            5: t5.trim(),
            6: t6.trim(),
          });
        }
      } catch {
        if (!cancelled) setDataUriByCard({ 1: null, 4: null, 5: null, 6: null });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const imageSrcFor = (n) => STATIC_CARD_IMAGES[n] || dataUriByCard[n] || null;

  const goPrev = useCallback(() => {
    setStartIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setStartIndex((i) => Math.min(maxStart, i + 1));
  }, [maxStart]);

  const visibleIndices = CARD_INDICES.slice(startIndex, startIndex + visibleCount);

  const gridClass =
    visibleCount >= 4
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      : visibleCount >= 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1';

  const canPrev = startIndex > 0;
  const canNext = startIndex < maxStart;

  const arrowBtnClass =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#4C078C] bg-transparent p-2 text-[#4C078C] transition hover:bg-[#4C078C]/5 disabled:cursor-not-allowed disabled:opacity-35';

  return (
    <div
      className="scroll-mt-28 bg-[#F9F9FB]"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="mx-auto w-full max-w-[1156px] px-6 py-[50px] sm:px-10 lg:px-[140px] lg:py-[70px]">
        <div className="flex flex-col gap-10 lg:gap-[60px]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45 }}
            className="flex w-full flex-col gap-4 sm:gap-6 lg:flex-row lg:items-start lg:justify-between"
          >
            <div className="flex max-w-[768px] flex-col gap-4 self-start text-start sm:gap-6">
              <h2 className="text-[32px] font-bold leading-tight sm:text-[36px] lg:text-[40px] lg:leading-[48px]" style={{ color: '#51516B' }}>
                {t('website.opportunitiesInOman.title')}
              </h2>
              <p className="max-w-[749px] text-base font-normal leading-6" style={{ color: '#51516B' }}>
                {t('website.opportunitiesInOman.subtitle')}
              </p>
            </div>
            <div
              className="flex shrink-0 gap-2 self-end lg:self-center"
              dir="ltr"
            >
              <button type="button" className={arrowBtnClass} onClick={goPrev} disabled={!canPrev} aria-label={t('website.opportunitiesInOman.sliderPrevAria')}>
                <ArrowLeftIcon />
              </button>
              <button type="button" className={arrowBtnClass} onClick={goNext} disabled={!canNext} aria-label={t('website.opportunitiesInOman.sliderNextAria')}>
                <ArrowRightIcon />
              </button>
            </div>
          </motion.div>

          <div className="min-h-[1px] w-full overflow-hidden">
            <AnimatePresence initial={false} mode="wait">
              <motion.ul
                key={`${startIndex}-${visibleCount}`}
                initial={{ opacity: 0, x: isRTL ? 12 : -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isRTL ? -12 : 12 }}
                transition={{ duration: 0.22 }}
                className={`grid list-none gap-8 sm:gap-x-7 sm:gap-y-10 lg:gap-x-[29px] ${gridClass}`}
              >
                {visibleIndices.map((n) => {
                  const src = imageSrcFor(n);
                  return (
                    <li key={n} className="flex min-w-0 flex-col gap-[17px]">
                      <div
                        className="relative flex aspect-[287/229] w-full flex-col items-end gap-2 overflow-hidden rounded-2xl bg-gradient-to-br from-[#E8E8EF] to-[#D4D4DE] p-3 sm:p-[10px]"
                        aria-label={t('website.opportunitiesInOman.placeholderImageAria', { n })}
                      >
                        {src ? (
                          <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        ) : (
                          <span
                            className="pointer-events-none absolute inset-0 flex items-center justify-center text-5xl font-bold tabular-nums text-[#51516B]/25 sm:text-6xl"
                            aria-hidden
                          >
                            {n}
                          </span>
                        )}
                        <div
                          className="relative z-[1] inline-flex max-w-[85%] items-center justify-center rounded-[12px] border border-white/60 px-4 py-2 shadow-[0_19px_39px_rgba(0,0,0,0.05)] backdrop-blur-[10px]"
                          style={{
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.20) 100%)',
                          }}
                        >
                          <span className="text-xs font-medium text-[#242424] sm:text-[11.5px]">
                            {t(`website.opportunitiesInOman.card${n}.sector`)}
                          </span>
                        </div>
                      </div>
                      <div className="flex min-w-0 flex-col gap-2.5 text-start">
                        <h3 className="text-xl font-semibold leading-tight text-[#242424] sm:text-[23px] sm:leading-[31px]">
                          {t(`website.opportunitiesInOman.card${n}.name`)}
                        </h3>
                        <div className="flex w-full flex-row items-center justify-between gap-3">
                          <span className="shrink-0 text-xs font-normal text-[#8B8B8B] sm:text-[12.5px]">
                            {t('website.opportunitiesInOman.capitalLabel')}
                          </span>
                          <span className="min-w-0 text-end text-[13.5px] font-semibold leading-5 text-[#363636] sm:text-[13.55px]">
                            {t(`website.opportunitiesInOman.card${n}.capital`)}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </motion.ul>
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.4 }}
            className="self-start text-start"
          >
            <a
              href={INVEST_OPPORTUNITIES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-[54px] items-center justify-center gap-1.5 rounded-full px-[18px] text-base font-semibold text-white transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C078C]/40"
              style={pillStyle}
            >
              {t('website.opportunitiesInOman.exploreCta')}
              <CtaChevron />
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OpportunitiesInOman;
